import './dsFocus.js';
import { Icon } from './Icon.jsx';

/**
 * Collapsible — the accordion section the design system was missing (CARD-INFO-TAB
 * P-A, spec D1). Reference: `LeadCard.dc.html` `.acc-row` / `.caret` (:20-25), the
 * chrome every Инфо-tab accordion of the blogger card rides on.
 *
 * CONTROLLED ONLY: `open` + `onToggle` are both required and no internal state exists.
 * An uncontrolled `defaultOpen` variant was rejected — P-B needs the header pencil to
 * force two accordions open, which only a controlled shape allows without a second API.
 *
 * Children render ONLY when open (unmount, not `display:none`), matching the
 * reference's `sc-if` and keeping hidden subtrees from running effects.
 *
 * The header is a real <button type="button"> carrying `aria-expanded` /
 * `aria-controls`; the body is a <div role="region"> with that id. Focus ring: shared,
 * via data-ds-focusable (MONITORING-DS spec S8).
 *
 * Props: `open` (bool), `onToggle` (fn), `title` (node), `count` (number|null →
 * ` (N)` in the muted hue), `meta` (node, right of the title row, before the caret),
 * `id` (string, the body's id and the header's aria-controls target).
 */
export function Collapsible ({ open, onToggle, title, count = null, meta = null, id, children, className }) {
  return (
    <div className={['oc-collapsible', className].filter(Boolean).join(' ')}>
      <button
        type="button"
        data-ds-focusable
        className="oc-collapsible__row"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span className="oc-collapsible__title">
          {title}
          {count != null && <span className="oc-collapsible__count">({count})</span>}
        </span>
        <span className="oc-collapsible__meta">
          {meta}
          <Icon
            name="caret-down"
            size="md"
            className="oc-collapsible__caret"
            data-open={open ? 'true' : 'false'}
          />
        </span>
      </button>
      {open && (
        <div id={id} role="region" className="oc-collapsible__body">
          {children}
        </div>
      )}
    </div>
  );
}
